import { z } from "zod";
import { IdentifiedOrDeletedAuthor } from "./author";
import { IsoDateString } from "./isoDate";

export const ChartType = z.enum(["LINE", "BAR", "PIE"]);
export type ChartType = z.infer<typeof ChartType>;

export const ChartPermissionRole = z.enum(["OWNER", "EDITOR", "VIEWER", "DENIED"]);
export type ChartPermissionRole = z.infer<typeof ChartPermissionRole>;

export const ChartPermissionTarget = z.enum(["USER", "ORGANIZATION", "PUBLIC", "COMPASS_TEAM"]);
export type ChartPermissionTarget = z.infer<typeof ChartPermissionTarget>;

export const ColumnDataType = z.enum([
    "string",
    "number",
    "boolean",
    "date_epoch",
    "date_iso",
    "percentage",
    "currency",
    "duration",
    "url",
    "email",
    "image_url",
    "color",
    "rating",
]);
export type ColumnDataType = z.infer<typeof ColumnDataType>;

export const ColumnRole = z.enum(["primary_key", "data", "display"]);
export type ColumnRole = z.infer<typeof ColumnRole>;

export const ColumnDefinition = z.object({
    name: z.string(),
    role: ColumnRole,
    dataType: ColumnDataType.default("string"),
});
export type ColumnDefinition = z.infer<typeof ColumnDefinition>;

export const DataRow = z.record(z.string(), z.string());
export type DataRow = z.infer<typeof DataRow>;

export const ChartDraftData = z.object({
    columns: z.array(ColumnDefinition),
    rows: z.array(DataRow),
});
export type ChartDraftData = z.infer<typeof ChartDraftData>;

const AuthorOutput = IdentifiedOrDeletedAuthor;

export const ChartPermissionOutput = z.object({
    id: z.number(),
    role: ChartPermissionRole,
    target: ChartPermissionTarget,
    userId: z.number().nullable(),
    organizationId: z.number().nullable(),
    userName: z.string().nullable(),
    organizationName: z.string().nullable(),
    organizationDeleted: z.boolean().default(false),
    userOrganizationName: z.string().nullable(),
    userOrganizationId: z.number().nullable(),
    grantedBy: AuthorOutput,
    createdAt: IsoDateString,
});
export type ChartPermissionOutput = z.infer<typeof ChartPermissionOutput>;

export const ChartAISummary = z.object({
    title: z.string(),
    summary: z.string(),
    trends: z.array(z.string()),
    recommendation: z.string(),
});
export type ChartAISummary = z.infer<typeof ChartAISummary>;

export const ChartVersionOutput = z.object({
    id: z.number(),
    chartId: z.number(),
    versionNumber: z.number(),
    label: z.string(),
    columns: z.array(ColumnDefinition),
    rows: z.array(DataRow),
    defaultChartType: ChartType,
    createdAt: IsoDateString,
    author: AuthorOutput,
    threadId: z.number().nullable(),
    aiSummary: ChartAISummary.nullable(),
    aiSummaryStatus: z.enum(["PENDING", "DONE", "ERROR"]).nullable(),
    aiSummaryGeneratedAt: z.string().nullable(),
    aiSummaryGeneratedBy: AuthorOutput.nullable(),
});
export type ChartVersionOutput = z.infer<typeof ChartVersionOutput>;

export const ChartSummary = z.object({
    id: z.number(),
    title: z.string(),
    description: z.string().nullable(),
    author: AuthorOutput,
    // Author's organization at query time. Surfaced in the Visualizations list
    // for Compass-team users only (PRTL-1178 / PRTL-1179).
    authorOrganization: z.object({ id: z.number(), name: z.string() }).nullable(),
    createdAt: IsoDateString,
    updatedAt: IsoDateString,
    versionCount: z.number(),
    activeVersionId: z.number().nullable(),
    activeVersion: z
        .object({
            aiSummary: ChartAISummary.nullable(),
        })
        .nullable(),
    hasDraft: z.boolean(),
    currentUserPermission: ChartPermissionRole.nullable(),
});
export type ChartSummary = z.infer<typeof ChartSummary>;

export const ChartDetail = ChartSummary.extend({
    draftData: ChartDraftData.nullable(),
    versions: z.array(ChartVersionOutput),
    permissions: z.array(ChartPermissionOutput),
});
export type ChartDetail = z.infer<typeof ChartDetail>;
